import React, { useState, useEffect } from "react";
import {
    Container,
    Typography,
    Paper,
    Box,
    Button,
    AppBar,
    Toolbar,
    Tabs,
    Tab,
    Divider,
    Card,
    CardContent,
    Grid,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Avatar,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress,
    Alert,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import {
    DatePicker,
    LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { signOut } from "firebase/auth";
import {
    collection,
    query,
    getDocs,
    doc,
    updateDoc,
    where,
    orderBy,
    deleteDoc,
    Timestamp,
    addDoc
} from "firebase/firestore";
import { auth, db } from "../firebase";
import {
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    EventNote as EventNoteIcon,
    AccessTime as AccessTimeIcon,
    Dashboard as DashboardIcon,
    FilterList as FilterListIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    CalendarToday as CalendarTodayIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import AdminCalendarView from "../components/AdminCalendarView";
import AdminTimeOffCreation from "../components/AdminTimeOffCreation";

const createSyntheticTimeEntries = async (orgId, timeOffRequest) => {
    try {
        const { startDate, endDate, userId, employeeEmail, type, typeLabel, id: requestId } = timeOffRequest;
        
        // Convert dates to ensure we have proper Date objects
        const start = startDate.toDate ? startDate.toDate() : new Date(startDate);
        const end = endDate.toDate ? endDate.toDate() : new Date(endDate);

        // Create entries for each day in the range
        const entries = [];
        const currentDate = new Date(start);

        while (currentDate <= end) {
            // Skip weekends (optional - remove if you want to include weekends)
            const dayOfWeek = currentDate.getDay();
            if (dayOfWeek !== 0 && dayOfWeek !== 6) { // 0 = Sunday, 6 = Saturday

                // Create clock-in time (9:00 AM)
                const clockInTime = new Date(currentDate);
                clockInTime.setHours(9, 0, 0, 0);

                // Create clock-out time (5:00 PM for 8-hour day)
                const clockOutTime = new Date(currentDate);
                clockOutTime.setHours(17, 0, 0, 0);

                // Clock In Entry
                entries.push({
                    userId,
                    email: employeeEmail,
                    type: "timeOff",
                    subType: "clockIn",
                    timeOffType: type,
                    timeOffLabel: typeLabel,
                    timeOffRequestId: requestId,
                    timestamp: Timestamp.fromDate(clockInTime),
                    syntheticEntry: true,
                    location: null
                });

                // Clock Out Entry
                entries.push({
                    userId,
                    email: employeeEmail,
                    type: "timeOff",
                    subType: "clockOut",
                    timeOffType: type,
                    timeOffLabel: typeLabel,
                    timeOffRequestId: requestId,
                    timestamp: Timestamp.fromDate(clockOutTime),
                    syntheticEntry: true,
                    location: null
                });
            }

            // Move to next day
            currentDate.setDate(currentDate.getDate() + 1);
        }

        // Batch write all entries
        const timeLogsRef = collection(db, "organizations", orgId, "timeLogs");
        const promises = entries.map(entry => addDoc(timeLogsRef, entry));

        await Promise.all(promises);
        
    } catch (error) {
        console.error("Error creating synthetic time entries:", error);
        throw error;
    }
};

const removeSyntheticTimeEntries = async (orgId, requestId) => {
    try {
        const timeLogsRef = collection(db, "organizations", orgId, "timeLogs");
        const q = query(
            timeLogsRef,
            where("timeOffRequestId", "==", requestId),
            where("syntheticEntry", "==", true)
        );

        const snapshot = await getDocs(q);
        const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));

        await Promise.all(deletePromises);
        console.log(`Removed ${snapshot.docs.length} synthetic entries for request ${requestId}`);
    } catch (error) {
        console.error("Error removing synthetic entries:", error);
    }
};

const AdminTimeOffDashboard = ({ orgId }) => {
    const [tabValue, setTabValue] = useState(0);
    const [showDateFilters, setShowDateFilters] = useState(false);
    const [timeOffRequests, setTimeOffRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        pending: 0,
        approved: 0,
        rejected: 0,
        total: 0
    });
    const [dateSorting, setDateSorting] = useState({
        field: null, // 'startDate', 'endDate', 'requestDate'
        direction: 'asc' // 'asc' or 'desc'
    });

    const [dateFiltering, setDateFiltering] = useState({
        startDateFrom: null,
        startDateTo: null,
        endDateFrom: null,
        endDateTo: null,
        requestDateFrom: null,
        requestDateTo: null
    });
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        employee: '',
        status: '',
        type: ''
    });
    const [employees, setEmployees] = useState([]);
    const [dialog, setDialog] = useState({
        open: false,
        request: null,
        action: ''
    });
    // Add alert state
    const [alert, setAlert] = useState({
        open: false,
        message: "",
        severity: "info"
    });

    useEffect(() => {
        if (orgId) {
            fetchTimeOffRequests();
        }
    }, [orgId]);

    useEffect(() => {
        if (orgId) {
            fetchEmployees();
        }
    }, [orgId]);

    const fetchTimeOffRequests = async () => {
        setLoading(true);
        try {
            const requestsRef = collection(db, "organizations", orgId, "timeOffRequests");
            const q = query(requestsRef, orderBy("requestDate", "desc"));
            const snapshot = await getDocs(q);

            const requests = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setTimeOffRequests(requests);

            // Calculate stats
            const pending = requests.filter(req => req.status === "pending").length;
            const approved = requests.filter(req => req.status === "approved").length;
            const rejected = requests.filter(req => req.status === "rejected").length;

            setStats({
                pending,
                approved,
                rejected,
                total: requests.length
            });

        } catch (err) {
            console.error("Error fetching time-off requests:", err);
        } finally {
            setLoading(false);
        }
    };
    
    const fetchEmployees = async () => {
        try {
            const employeesRef = collection(db, "userOrgs");
            const q = query(employeesRef, where("orgId", "==", orgId));
            const employeesSnapshot = await getDocs(q);

            const employeesList = employeesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setEmployees(employeesList);
        } catch (error) {
            console.error("Error fetching employees:", error);
        }
    };

    const handleDateSort = (field) => {
        setDateSorting(prev => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const sortRequestsByDate = (requests) => {
        if (!dateSorting.field) return requests;

        return [...requests].sort((a, b) => {
            let dateA, dateB;

            switch (dateSorting.field) {
                case 'startDate':
                    dateA = a.startDate?.toDate?.() || new Date(a.startDate);
                    dateB = b.startDate?.toDate?.() || new Date(b.startDate);
                    break;
                case 'endDate':
                    dateA = a.endDate?.toDate?.() || new Date(a.endDate);
                    dateB = b.endDate?.toDate?.() || new Date(b.endDate);
                    break;
                case 'requestDate':
                    dateA = a.requestDate?.toDate?.() || new Date(a.requestDate);
                    dateB = b.requestDate?.toDate?.() || new Date(b.requestDate);
                    break;
                default:
                    return 0;
            }

            const comparison = dateA - dateB;
            return dateSorting.direction === 'asc' ? comparison : -comparison;
        });
    };

    const filterRequestsByDate = (requests) => {
        return requests.filter(req => {
            const startDate = req.startDate?.toDate?.() || new Date(req.startDate);
            const endDate = req.endDate?.toDate?.() || new Date(req.endDate);
            const requestDate = req.requestDate?.toDate?.() || new Date(req.requestDate);

            // Filter by start date range
            if (dateFiltering.startDateFrom && startDate < dateFiltering.startDateFrom) return false;
            if (dateFiltering.startDateTo && startDate > dateFiltering.startDateTo) return false;

            // Filter by end date range
            if (dateFiltering.endDateFrom && endDate < dateFiltering.endDateFrom) return false;
            if (dateFiltering.endDateTo && endDate > dateFiltering.endDateTo) return false;

            // Filter by request date range
            if (dateFiltering.requestDateFrom && requestDate < dateFiltering.requestDateFrom) return false;
            if (dateFiltering.requestDateTo && requestDate > dateFiltering.requestDateTo) return false;

            return true;
        });
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleLogout = async () => {
        await signOut(auth);
        window.location.href = "/login";
    };

    const handleBack = () => {
        window.location.href = "/admin";
    };

    const openDialog = (request, action) => {
        setDialog({
            open: true,
            request,
            action
        });
    };

    const closeDialog = () => {
        setDialog({
            ...dialog,
            open: false
        });
    };

    const handleRequestAction = async () => {
        if (!dialog.request || !dialog.action) return;

        try {
            const requestRef = doc(db, "organizations", orgId, "timeOffRequests", dialog.request.id);
            await updateDoc(requestRef, {
                status: dialog.action,
                responseDate: new Date(),
            });

            // NEW: Create synthetic time log entries if approved
            if (dialog.action === "approved") {
                try {
                    await createSyntheticTimeEntries(orgId, dialog.request);
                    console.log("Synthetic time entries created successfully");
                } catch (syntheticError) {
                    console.error("Failed to create synthetic entries:", syntheticError);
                    // Don't fail the approval, just log the error
                    setAlert({
                        open: true,
                        message: "Time off approved, but failed to sync with time logs. Please contact support.",
                        severity: "warning"
                    });
                }
            }

            if (dialog.action === "rejected") {
                try {
                    await removeSyntheticTimeEntries(orgId, dialog.request.id);
                } catch (cleanupError) {
                    // Don't fail the rejection, just log
                }
            }

            // Update local state
            const updatedRequests = timeOffRequests.map(req =>
                req.id === dialog.request.id
                    ? { ...req, status: dialog.action }
                    : req
            );

            setTimeOffRequests(updatedRequests);

            // Update stats
            const pending = updatedRequests.filter(req => req.status === "pending").length;
            const approved = updatedRequests.filter(req => req.status === "approved").length;
            const rejected = updatedRequests.filter(req => req.status === "rejected").length;

            setStats({
                pending,
                approved,
                rejected,
                total: updatedRequests.length
            });

            setAlert({
                open: true,
                message: `Time off request ${dialog.action} successfully`,
                severity: "success"
            });

            closeDialog();
        } catch (err) {
            console.error("Error updating request:", err);
            setAlert({
                open: true,
                message: "Failed to update time off request",
                severity: "error"
            });
        }
    };

    // Add delete functionality
    const handleDeleteRequest = async (requestId) => {
        if (!window.confirm("Are you sure you want to delete this time off request?")) {
            return;
        }

        setLoading(true);
        try {
            const requestRef = doc(db, "organizations", orgId, "timeOffRequests", requestId);
            await deleteDoc(requestRef);

            // Update the local state to remove the deleted request
            const updatedRequests = timeOffRequests.filter(req => req.id !== requestId);
            setTimeOffRequests(updatedRequests);

            // Update stats
            const pending = updatedRequests.filter(req => req.status === "pending").length;
            const approved = updatedRequests.filter(req => req.status === "approved").length;
            const rejected = updatedRequests.filter(req => req.status === "rejected").length;

            setStats({
                pending,
                approved,
                rejected,
                total: updatedRequests.length
            });

            // Show success message
            setAlert({
                open: true,
                message: "Time off request deleted successfully",
                severity: "success"
            });
        } catch (err) {
            console.error("Error deleting time-off request:", err);
            setAlert({
                open: true,
                message: "Failed to delete time off request",
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    // Close alert handler
    const handleCloseAlert = () => {
        setAlert({
            ...alert,
            open: false
        });
    };

    const formatDate = (date) => {
        if (!date) return "N/A";
        // Handle both Timestamp objects and regular Date objects
        const dateObj = date.toDate ? date.toDate() : new Date(date);
        return dateObj.toLocaleDateString();
    };

    // Function to calculate duration between two dates
    const calculateDuration = (startDate, endDate) => {
        if (!startDate || !endDate) return "N/A";

        const start = startDate.toDate ? startDate.toDate() : new Date(startDate);
        const end = endDate.toDate ? endDate.toDate() : new Date(endDate);

        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates

        return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    };

    const getFilteredRequests = () => {
        let filtered = timeOffRequests;

        // Filter by tab first
        if (tabValue === 1) {
            filtered = filtered.filter(req => req.status === "pending");
        } else if (tabValue === 2) {
            filtered = filtered.filter(req => req.status === "approved");
        } else if (tabValue === 3) {
            filtered = filtered.filter(req => req.status === "rejected");
        }

        // Apply additional filters
        if (filters.startDate) {
            filtered = filtered.filter(req => {
                const reqStartDate = req.startDate?.toDate?.() || new Date(req.startDate);
                return reqStartDate >= filters.startDate;
            });
        }

        if (filters.endDate) {
            filtered = filtered.filter(req => {
                const reqEndDate = req.endDate?.toDate?.() || new Date(req.endDate);
                return reqEndDate <= filters.endDate;
            });
        }

        if (filters.employee) {
            filtered = filtered.filter(req =>
                req.employeeEmail?.toLowerCase().includes(filters.employee.toLowerCase()) ||
                req.employeeName?.toLowerCase().includes(filters.employee.toLowerCase())
            );
        }

        if (filters.status) {
            filtered = filtered.filter(req => req.status === filters.status);
        }

        if (filters.type) {
            filtered = filtered.filter(req => req.type === filters.type);
        }

        // Apply date filtering
        filtered = filterRequestsByDate(filtered);

        // Apply date sorting
        filtered = sortRequestsByDate(filtered);

        return filtered;
    };

    const filteredRequests = getFilteredRequests();

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const EnhancedFilters = () => (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Filter Requests
                </Typography>

                <Grid container spacing={2}>
                    {/* Date Range Filters */}
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePicker
                            label="Start Date"
                            value={filters.startDate}
                            onChange={(newValue) => handleFilterChange('startDate', newValue)}
                            slotProps={{
                                textField: { fullWidth: true, size: "small" }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <DatePicker
                            label="End Date"
                            value={filters.endDate}
                            onChange={(newValue) => handleFilterChange('endDate', newValue)}
                            slotProps={{
                                textField: { fullWidth: true, size: "small" }
                            }}
                        />
                    </Grid>

                    {/* Employee Filter */}
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Employee</InputLabel>
                            <Select
                                value={filters.employee}
                                onChange={(e) => handleFilterChange('employee', e.target.value)}
                                label="Employee"
                            >
                                <MenuItem value="">All Employees</MenuItem>
                                {employees.map((emp) => (
                                    <MenuItem key={emp.id} value={emp.email}>
                                        {emp.name || emp.email}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Status Filter */}
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="approved">Approved</MenuItem>
                                <MenuItem value="rejected">Rejected</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Type Filter */}
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={filters.type}
                                onChange={(e) => handleFilterChange('type', e.target.value)}
                                label="Type"
                            >
                                <MenuItem value="">All Types</MenuItem>
                                <MenuItem value="vacation">Vacation</MenuItem>
                                <MenuItem value="sick">Sick</MenuItem>
                                <MenuItem value="personal">Personal</MenuItem>
                                <MenuItem value="bereavement">Bereavement</MenuItem>
                                <MenuItem value="maternity">Maternity</MenuItem>
                                <MenuItem value="paternity">Paternity</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {/* Filter Actions */}
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<FilterListIcon />}
                        onClick={() => setShowDateFilters(!showDateFilters)}
                        sx={{ ml: 1 }}
                    >
                        {showDateFilters ? 'Hide' : 'Show'} Date Filters
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={clearAllFilters}
                        size="small"
                    >
                        Clear All Filters
                    </Button>

                    <Chip
                        label={`${filteredRequests.length} results`}
                        color="primary"
                        variant="outlined"
                    />
                </Box>
            </Paper>
        </LocalizationProvider>
    );

    const DateFiltersComponent = () => (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Advanced Date Filters
                    </Typography>
                    <Button
                        size="small"
                        startIcon={<ClearIcon />}
                        onClick={() => setDateFiltering({
                            startDateFrom: null,
                            startDateTo: null,
                            endDateFrom: null,
                            endDateTo: null,
                            requestDateFrom: null,
                            requestDateTo: null
                        })}
                    >
                        Clear Date Filters
                    </Button>
                </Box>

                <Grid container spacing={2}>
                    {/* Start Date Range */}
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>
                            Filter by Start Date
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <DatePicker
                                label="From"
                                value={dateFiltering.startDateFrom}
                                onChange={(newValue) => setDateFiltering(prev => ({
                                    ...prev,
                                    startDateFrom: newValue
                                }))}
                                slotProps={{
                                    textField: { size: "small", fullWidth: true }
                                }}
                            />
                            <DatePicker
                                label="To"
                                value={dateFiltering.startDateTo}
                                onChange={(newValue) => setDateFiltering(prev => ({
                                    ...prev,
                                    startDateTo: newValue
                                }))}
                                slotProps={{
                                    textField: { size: "small", fullWidth: true }
                                }}
                            />
                        </Box>
                    </Grid>

                    {/* End Date Range */}
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>
                            Filter by End Date
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <DatePicker
                                label="From"
                                value={dateFiltering.endDateFrom}
                                onChange={(newValue) => setDateFiltering(prev => ({
                                    ...prev,
                                    endDateFrom: newValue
                                }))}
                                slotProps={{
                                    textField: { size: "small", fullWidth: true }
                                }}
                            />
                            <DatePicker
                                label="To"
                                value={dateFiltering.endDateTo}
                                onChange={(newValue) => setDateFiltering(prev => ({
                                    ...prev,
                                    endDateTo: newValue
                                }))}
                                slotProps={{
                                    textField: { size: "small", fullWidth: true }
                                }}
                            />
                        </Box>
                    </Grid>

                    {/* Request Date Range */}
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>
                            Filter by Request Date
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <DatePicker
                                label="From"
                                value={dateFiltering.requestDateFrom}
                                onChange={(newValue) => setDateFiltering(prev => ({
                                    ...prev,
                                    requestDateFrom: newValue
                                }))}
                                slotProps={{
                                    textField: { size: "small", fullWidth: true }
                                }}
                            />
                            <DatePicker
                                label="To"
                                value={dateFiltering.requestDateTo}
                                onChange={(newValue) => setDateFiltering(prev => ({
                                    ...prev,
                                    requestDateTo: newValue
                                }))}
                                slotProps={{
                                    textField: { size: "small", fullWidth: true }
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>

                {/* Active Filters Display */}
                {(dateFiltering.startDateFrom || dateFiltering.endDateFrom || dateFiltering.requestDateFrom) && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Active date filters: {Object.values(dateFiltering).filter(Boolean).length} applied
                        </Typography>
                    </Box>
                )}
            </Paper>
        </LocalizationProvider>
    );

    const clearAllFilters = () => {
        setFilters({
            startDate: null,
            endDate: null,
            employee: '',
            status: '',
            type: ''
        });
    };

    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Button
                        color="inherit"
                        onClick={handleBack}
                        startIcon={<ArrowBackIcon />}
                        sx={{ mr: 2 }}
                    >
                        Back to Dashboard
                    </Button>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        TimeWarden Admin
                    </Typography>
                    <Button color="inherit" onClick={handleLogout}>Logout</Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg">
                <Paper sx={{ mt: 5, p: 4, borderRadius: 2, boxShadow: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <EventNoteIcon fontSize="large" sx={{ mr: 2, color: 'primary.main' }} />
                            <Typography variant="h4" component="h1">
                                Time Off Management
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={fetchTimeOffRequests}
                        >
                            Refresh
                        </Button>
                    </Box>

                    <Divider sx={{ mb: 4 }} />

                    <EnhancedFilters />

                    {showDateFilters && <DateFiltersComponent />}

                    {/* Stats Cards */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={3}>
                            <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', height: '100%' }}>
                                <CardContent>
                                    <Typography color="inherit" gutterBottom>
                                        Total Requests
                                    </Typography>
                                    <Typography variant="h3">
                                        {stats.total}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText', height: '100%' }}>
                                <CardContent>
                                    <Typography color="inherit" gutterBottom>
                                        Pending
                                    </Typography>
                                    <Typography variant="h3">
                                        {stats.pending}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText', height: '100%' }}>
                                <CardContent>
                                    <Typography color="inherit" gutterBottom>
                                        Approved
                                    </Typography>
                                    <Typography variant="h3">
                                        {stats.approved}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText', height: '100%' }}>
                                <CardContent>
                                    <Typography color="inherit" gutterBottom>
                                        Rejected
                                    </Typography>
                                    <Typography variant="h3">
                                        {stats.rejected}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {(dateSorting.field || Object.values(dateFiltering).some(Boolean)) && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {dateSorting.field && (
                                    <Chip
                                        label={`Sorted by ${dateSorting.field} (${dateSorting.direction})`}
                                        onDelete={() => setDateSorting({ field: null, direction: 'asc' })}
                                        color="primary"
                                        size="small"
                                    />
                                )}
                                {Object.values(dateFiltering).some(Boolean) && (
                                    <Chip
                                        label={`${Object.values(dateFiltering).filter(Boolean).length} date filters active`}
                                        color="secondary"
                                        size="small"
                                    />
                                )}
                            </Box>
                        </Alert>
                    )}

                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            sx={{ mb: 2 }}
                        >
                            <Tab label="All Requests" />
                            <Tab
                                label="Pending"
                                icon={stats.pending > 0 ? <Chip size="small" label={stats.pending} color="warning" /> : null}
                                iconPosition="end"
                            />
                            <Tab label="Approved" />
                            <Tab label="Rejected" />                            
                            <Tab
                                label="Create Time Off"
                                icon={<AddIcon />}
                                iconPosition="start"
                            />
                            <Tab
                                label="Calendar View"
                                icon={<CalendarTodayIcon />}
                                iconPosition="start"
                            />
                        </Tabs>

                        <Button startIcon={<FilterListIcon />} variant="outlined">
                            Filter
                        </Button>
                    </Box>

                    {/* Table of requests - only show if not on Create tab */}
                    {tabValue !== 4 && tabValue !== 5 && (  
                        loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                                <CircularProgress />
                            </Box>
                        ) : filteredRequests.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="h6" color="text.secondary">
                                    No time-off requests found
                                </Typography>
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Employee</TableCell>
                                            <TableCell>Request Type</TableCell>
                                            {/* Enhanced Dates Column with Sorting */}
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                        Dates
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Button
                                                            size="small"
                                                            variant={dateSorting.field === 'startDate' ? 'contained' : 'outlined'}
                                                            onClick={() => handleDateSort('startDate')}
                                                            startIcon={
                                                                dateSorting.field === 'startDate' ? (
                                                                    dateSorting.direction === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />
                                                                ) : <FilterListIcon />
                                                            }
                                                            sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
                                                        >
                                                            Start
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant={dateSorting.field === 'endDate' ? 'contained' : 'outlined'}
                                                            onClick={() => handleDateSort('endDate')}
                                                            startIcon={
                                                                dateSorting.field === 'endDate' ? (
                                                                    dateSorting.direction === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />
                                                                ) : <FilterListIcon />
                                                            }
                                                            sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
                                                        >
                                                            End
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>Duration</TableCell>
                                            <TableCell>Reason</TableCell>
                                            <TableCell>Status</TableCell>
                                            {/* Request Date Column with Sorting */}
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant={dateSorting.field === 'requestDate' ? 'contained' : 'outlined'}
                                                    onClick={() => handleDateSort('requestDate')}
                                                    startIcon={
                                                        dateSorting.field === 'requestDate' ? (
                                                            dateSorting.direction === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />
                                                        ) : <FilterListIcon />
                                                    }
                                                    sx={{ fontSize: '0.75rem' }}
                                                >
                                                    Request Date
                                                </Button>
                                            </TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredRequests.map((request) => (
                                            <TableRow key={request.id}>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                                            {request.employeeName ? request.employeeName.charAt(0) : "U"}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body1">
                                                                {request.employeeName || "Unknown"}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {request.employeeEmail}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{request.type || "Vacation"}</TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            <AccessTimeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                                            From: {formatDate(request.startDate)}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            <AccessTimeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                                            To: {formatDate(request.endDate)}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{calculateDuration(request.startDate, request.endDate)}</TableCell>
                                                <TableCell>{request.reason || "Not specified"}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={request.status || "pending"}
                                                        color={
                                                            request.status === "approved" ? "success" :
                                                                request.status === "rejected" ? "error" :
                                                                    "warning"
                                                        }
                                                    />
                                                </TableCell>
                                                {/* ADD THIS NEW TABLECELL HERE */}
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatDate(request.requestDate)}
                                                    </Typography>
                                                    {request.responseDate && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Responded: {formatDate(request.responseDate)}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex' }}>
                                                        {request.status === "pending" && (
                                                            <>
                                                                <IconButton
                                                                    color="success"
                                                                    onClick={() => openDialog(request, "approved")}
                                                                    title="Approve"
                                                                >
                                                                    <CheckCircleIcon />
                                                                </IconButton>
                                                                <IconButton
                                                                    color="error"
                                                                    onClick={() => openDialog(request, "rejected")}
                                                                    title="Reject"
                                                                >
                                                                    <CancelIcon />
                                                                </IconButton>
                                                            </>
                                                        )}
                                                        {/* Add delete button for all requests */}
                                                        <IconButton
                                                            color="error"
                                                            onClick={() => handleDeleteRequest(request.id)}
                                                            title="Delete"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )
                    )}

                    {/* Create Time Off tab content */}
                    {tabValue === 4 && (
                        <Box sx={{ mt: 2 }}>
                            <AdminTimeOffCreation orgId={orgId} />
                        </Box>
                    )}
                    {tabValue === 5 && (
                        <Box sx={{ mt: 2 }}>
                            <AdminCalendarView orgId={orgId} />
                        </Box>
                    )}
                </Paper>
            </Container>

            {/* Alert Snackbar */}
            <Snackbar
                open={alert.open}
                autoHideDuration={6000}
                onClose={handleCloseAlert}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
                    {alert.message}
                </Alert>
            </Snackbar>

            {/* Confirmation Dialog */}
            <Dialog
                open={dialog.open}
                onClose={closeDialog}
            >
                <DialogTitle>
                    {dialog.action === "approved" ? "Approve Time-Off Request" : "Reject Time-Off Request"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {dialog.action === "approved"
                            ? "Are you sure you want to approve this time-off request?"
                            : "Are you sure you want to reject this time-off request?"}
                    </DialogContentText>
                    {dialog.request && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                <strong>Employee:</strong> {dialog.request.employeeName || dialog.request.employeeEmail || "Unknown"}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Type:</strong> {dialog.request.type || "Vacation"}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Dates:</strong> {formatDate(dialog.request.startDate)} to {formatDate(dialog.request.endDate)}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Reason:</strong> {dialog.request.reason || "Not specified"}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={handleRequestAction}
                        color={dialog.action === "approved" ? "success" : "error"}
                        variant="contained"
                    >
                        {dialog.action === "approved" ? "Approve" : "Reject"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AdminTimeOffDashboard;